import type { AinaAndroidFeatureFlags } from "./ainaNativeLaunchProps";

/** Firebase Remote Config key for the JSON blob that lists learn features (includes AINA mic/doubts). */
export const LEARN_FEATURES_ENABLE_RC_KEY = "learn_features_enable";

type LearnFeatureRow = {
  feature?: string;
  enable?: unknown;
};

function coerceEnable(v: unknown): boolean | undefined {
  if (v === undefined || v === null) {
    return undefined;
  }
  if (typeof v === "boolean") {
    return v;
  }
  if (typeof v === "number") {
    return v !== 0;
  }
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1") {
      return true;
    }
    if (s === "false" || s === "0") {
      return false;
    }
  }
  return undefined;
}

/** Parse RC value (stringified JSON or object) into a learn-features root object. */
export function parseLearnFeaturesEnablePayload(
  raw: unknown,
): Record<string, unknown> | null {
  if (raw == null || raw === "") {
    return null;
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw !== "object") {
    return null;
  }
  return raw as Record<string, unknown>;
}

export function partialAinaFlagsFromLearnFeaturesPayload(
  raw: unknown,
): Partial<AinaAndroidFeatureFlags> {
  const obj = parseLearnFeaturesEnablePayload(raw);
  if (!obj) {
    return {};
  }
  const features_data_raw = obj.features_data ?? obj.featuresData;
  if (!Array.isArray(features_data_raw)) {
    return {};
  }
  const features_data = features_data_raw;

  let disableMic: boolean | undefined;
  let disableMicFromVoiceRow = false;
  let disableDoubts: boolean | undefined;
  let disableDoubtsFromVoiceRow = false;

  for (const row of features_data) {
    if (row == null || typeof row !== "object") {
      continue;
    }
    const r = row as LearnFeatureRow;
    const name = String(r.feature ?? "")
      .trim()
      .toLowerCase();
    if (!name) {
      continue;
    }
    const enabled = coerceEnable(r.enable);
    if (enabled === undefined) {
      continue;
    }

    switch (name) {
      case "ainadisablemic":
        disableMic = enabled;
        disableMicFromVoiceRow = true;
        break;
      case "ainamentordisablemic":
        if (!disableMicFromVoiceRow) {
          disableMic = enabled;
        }
        break;
      case "ainadisabledoubts":
        disableDoubts = enabled;
        disableDoubtsFromVoiceRow = true;
        break;
      case "ainamentordisabledoubts":
      case "and_ainadisabledoubts":
        if (!disableDoubtsFromVoiceRow) {
          disableDoubts = enabled;
        }
        break;
    }
  }

  const partial: Partial<AinaAndroidFeatureFlags> = {};
  if (disableMic !== undefined) {
    partial.ainaDisableMic = disableMic;
  }
  if (disableDoubts !== undefined) {
    partial.ainaDisableDoubts = disableDoubts;
  }
  return partial;
}

export function isLearnFeatureEnabled(
  raw: unknown,
  featureName: string,
  defaultValue = false,
): boolean {
  const obj = parseLearnFeaturesEnablePayload(raw);
  if (!obj) {
    return defaultValue;
  }
  const features_data_raw = obj.features_data ?? obj.featuresData;
  if (!Array.isArray(features_data_raw)) {
    return defaultValue;
  }

  const target = featureName.trim().toLowerCase();
  if (!target) {
    return defaultValue;
  }

  for (const row of features_data_raw) {
    if (row == null || typeof row !== "object") {
      continue;
    }
    const r = row as LearnFeatureRow;
    const name = String(r.feature ?? "")
      .trim()
      .toLowerCase();
    if (name !== target) {
      continue;
    }
    const enabled = coerceEnable(r.enable);
    return enabled ?? defaultValue;
  }

  return defaultValue;
}

export function mergeAinaFeatureFlagsWithLearnPartial(
  base: AinaAndroidFeatureFlags,
  learn: Partial<AinaAndroidFeatureFlags>,
): AinaAndroidFeatureFlags {
  return {
    ainaDisableMic:
      learn.ainaDisableMic !== undefined
        ? learn.ainaDisableMic
        : base.ainaDisableMic,
    ainaDisableDoubts:
      learn.ainaDisableDoubts !== undefined
        ? learn.ainaDisableDoubts
        : base.ainaDisableDoubts,
  };
}
